import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api, downloadFile } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, FileSpreadsheet, Landmark, Layers, Package, Star, X, Pin, Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import CompactMoneyToggle from "@/components/CompactMoneyToggle";
import MailtoAttachmentModal from "@/components/MailtoAttachmentModal";
import PnLView from "@/components/reports/PnLView";
import BalanceSheetView from "@/components/reports/BalanceSheetView";
import ExpenseView from "@/components/reports/ExpenseView";
import CashFlowView from "@/components/reports/CashFlowView";
import IncomeStatementView from "@/components/reports/IncomeStatementView";
import T12View from "@/components/reports/T12View";
import ARAgingView from "@/components/reports/ARAgingView";
import GLView from "@/components/reports/GLView";
import TrialBalanceView from "@/components/reports/TrialBalanceView";

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ["pnl", "is", "bs", "exp", "cf", "t12", "ar", "gl", "tb"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "gl";
  const [tab, setTab] = useState(initialTab);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [accountId, setAccountId] = useState("");  // "" = all accounts
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [defaultApplied, setDefaultApplied] = useState(false);
  const [pnl, setPnl] = useState(null);
  const [income, setIncome] = useState(null);
  const [bs, setBs] = useState(null);
  const [exp, setExp] = useState(null);
  const [cf, setCf] = useState(null);
  const [t12, setT12] = useState(null);
  const [arAging, setArAging] = useState(null);
  const [gl, setGl] = useState(null);
  const [tb, setTb] = useState(null);
  const [glCategories, setGlCategories] = useState([]);  // chip multi-select for GL
  const [glCompare, setGlCompare] = useState(null);      // null | "prior_period" | "prior_year"
  const [isCompare, setIsCompare] = useState(null); // null | "prior_period" | "prior_year"
  const [loading, setLoading] = useState(true);

  // Share-with-client modal state
  const [shareOpen, setShareOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [shareBulk, setShareBulk] = useState(false);
  const [shareForm, setShareForm] = useState({
    client_user_id: "", report_kind: "pnl", file_type: "pdf", note: "",
  });
  const [sharing, setSharing] = useState(false);
  // Mail-by-email (mailto) state
  const [mailtoDraft, setMailtoDraft] = useState(null);
  const [mailtoBusy, setMailtoBusy] = useState(false);
  useEffect(() => {
    if (!shareOpen) return;
    (async () => {
      try {
        const r = await api.get("/users");
        setClients((r.data || []).filter((u) => u.role === "client" && u.is_active));
      } catch { /* silent */ }
    })();
  }, [shareOpen]);
  const shareReport = async () => {
    if (!shareBulk && !shareForm.client_user_id) {
      toast.error("Pick a client or turn on 'Publish to all'"); return;
    }
    setSharing(true);
    try {
      const payload = {
        client_user_id: shareBulk ? "bulk" : shareForm.client_user_id,
        report_kind: shareForm.report_kind,
        file_type: shareForm.file_type,
        period_start: start || null,
        period_end: end || null,
        note: shareForm.note || null,
      };
      if (shareBulk) {
        const r = await api.post("/shared-reports/bulk", payload);
        toast.success(`${r.data.title || "Report"} shared with ${r.data.count} client${r.data.count === 1 ? "" : "s"}`);
      } else {
        const r = await api.post("/shared-reports", payload);
        toast.success(`${r.data.title} shared with client`);
      }
      setShareOpen(false);
      setShareForm({ client_user_id: "", report_kind: "pnl", file_type: "pdf", note: "" });
      setShareBulk(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Share failed");
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    setSearchParams(tab === "gl" ? {} : { tab }, { replace: true });
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/bank-accounts");
        setAccounts(r.data);
      } catch {}
    })();
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const r = await api.get("/close-packages");
      setTemplates(r.data);
    } catch {}
  };

  const saveCurrentAsTemplate = async () => {
    const name = window.prompt("Name this close package template:\n(e.g. 'Monthly Investor Pack', 'Quarterly Tax Pack')");
    if (!name?.trim()) return;
    try {
      await api.post("/close-packages", {
        name: name.trim(),
        period_start: start || null,
        period_end: end || null,
        bank_account_id: accountId || null,
      });
      toast.success("Template saved");
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save template");
    }
  };

  const applyTemplate = (tpl) => {
    setStart(tpl.period_start || "");
    setEnd(tpl.period_end || "");
    setAccountId(tpl.bank_account_id || "");
    toast.info(`Applied "${tpl.name}"`);
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete the template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/close-packages/${tpl.id}`);
      toast.success("Template removed");
      loadTemplates();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const togglePin = async (tpl) => {
    try {
      await api.patch(`/close-packages/${tpl.id}/default`);
      toast.success(tpl.is_default ? "Unpinned as default" : "Pinned as default");
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to toggle pin");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (accountId) params.bank_account_id = accountId;
      const isParams = { ...params };
      if (isCompare) isParams.compare = isCompare;
      // T-12 is fixed to 12 months, so start/end filters don't apply — only bank_account_id.
      const t12Params = accountId ? { bank_account_id: accountId } : {};
      const bsParams = { ...params };
      if (isCompare) bsParams.compare = isCompare;
      const [a, b, c, d, e, f, g, h, i] = await Promise.all([
        api.get("/reports/pnl", { params }),
        api.get("/reports/balance-sheet", { params: bsParams }),
        api.get("/reports/expenses", { params }),
        api.get("/reports/cash-flow", { params }),
        api.get("/reports/income-statement", { params: isParams }),
        api.get("/reports/t12", { params: t12Params }),
        api.get("/reports/ar-aging", { params: end ? { as_of: end } : {} }),
        api.get("/reports/gl", { params: {
          ...params,
          ...(glCategories.length ? { categories: glCategories.join(",") } : {}),
          ...(glCompare ? { compare: glCompare } : {}),
        } }),
        api.get("/reports/trial-balance", { params }),
      ]);
      setPnl(a.data); setBs(b.data); setExp(c.data); setCf(d.data);
      setIncome(e.data); setT12(f.data); setArAging(g.data); setGl(h.data); setTb(i.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, accountId, isCompare, glCategories, glCompare]);

  const exportReport = async (kind, format) => {
    try {
      const params = {};
      if (kind === "ar-aging") {
        if (end) params.as_of = end;
      } else {
        // T-12 doesn't take start/end — it's always 12 months
        if (kind !== "t12") {
          if (start) params.start = start;
          if (end) params.end = end;
        }
        if (accountId) params.bank_account_id = accountId;
      }
      params.format = format;
      if (kind === "income-statement" && isCompare) params.compare = isCompare;
      if (kind === "gl" && glCategories.length) params.categories = glCategories.join(",");
      if (kind === "gl" && glCompare) params.compare = glCompare;
      const qs = new URLSearchParams(params).toString();
      const ext = format === "pdf" ? "pdf" : "xlsx";
      await downloadFile(`/reports/${kind}/export?${qs}`, `${kind}.${ext}`);
    } catch { toast.error("Export failed"); }
  };

  const shareReportByEmail = async (kind) => {
    if (mailtoBusy) return;
    setMailtoBusy(true);
    try {
      // 1) Check the user's mail-routing preference for the "reports" flow
      const prefs = await api.get("/mail-preferences");
      const mode = prefs.data?.reports || "system";
      if (mode !== "mailto") {
        // 'system' delivery → fall back to the existing publish-to-client modal
        setShareForm((f) => ({ ...f, report_kind: mapKindToShareKind(kind) }));
        setShareOpen(true);
        return;
      }
      // 2) mailto delivery → fetch the draft text (subject + body)
      const params = {};
      if (kind === "ar-aging") {
        if (end) params.end = end;
      } else {
        if (kind !== "t12") {
          if (start) params.start = start;
          if (end) params.end = end;
        }
      }
      if (kind === "gl" && glCategories.length) params.categories = glCategories.join(",");
      const draftRes = await api.get("/reports/mailto-draft", {
        params: { kind, ...params },
      });
      const draft = draftRes.data;
      // 3) Build the mailto: URL (empty recipient — accountant pastes it in)
      const encoded = `mailto:?subject=${encodeURIComponent(draft.email_subject)}&body=${encodeURIComponent(draft.email_body)}`;
      // 4) Open the attachment-copy modal
      setMailtoDraft({
        title: `Share ${draft.title} by email`,
        subtitle: `Period: ${draft.period_label}`,
        to: "",
        subject: draft.email_subject,
        body: draft.email_body,
        mailtoUrl: encoded,
        pdfDownloadUrl: `/reports/${kind}/export?${new URLSearchParams({ ...params, format: "pdf" }).toString()}`,
        pdfFilename: draft.pdf_filename,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not prepare email draft");
    } finally {
      setMailtoBusy(false);
    }
  };

  // Convert on-screen export-kind names to the backend shared-reports kind names
  const mapKindToShareKind = (kind) => {
    const map = { "pnl": "pnl", "balance-sheet": "balance_sheet", "expenses": "expenses",
                  "cash-flow": "cash_flow", "income-statement": "income_statement",
                  "t12": "pnl", "ar-aging": "pnl", "gl": "pnl", "trial-balance": "pnl" };
    return map[kind] || "pnl";
  };

  const exportClosePackage = async (format) => {
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (accountId) params.bank_account_id = accountId;
      params.format = format;
      const qs = new URLSearchParams(params).toString();
      const ext = format === "pdf" ? "pdf" : "xlsx";
      await downloadFile(`/reports/close-package/export?${qs}`, `period_close_package.${ext}`);
      toast.success("Close package downloaded");
    } catch { toast.error("Could not generate close package"); }
  };

  const selectedAccount = accounts.find((a) => a.id === accountId);

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Financial statements"
        testid="reports-header"
        actions={
          <>
            <CompactMoneyToggle testid="reports-compact-toggle" />
            <button
              onClick={() => setShareOpen(true)}
              data-testid="reports-share-btn"
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <Send size={14} /> Share with client
            </button>
          </>
        }
      />
      <PageBody>
        <div
          className="ll-card-flat p-4 mb-6 flex flex-wrap items-stretch sm:items-end gap-3"
          data-testid="reports-filters"
          style={{ background: "#FEF2F2" }}
        >
          {/* Account scope */}
          <div className="w-full sm:flex-1 sm:min-w-[280px]">
            <label className="text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1 flex items-center gap-1.5">
              <Landmark size={12} /> Account scope
            </label>
            <div className="flex items-center gap-2 min-w-0">
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                data-testid="reports-account-select"
                className="flex-1 min-w-0 px-3 py-2 border bg-white text-sm"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <option value="">All bank accounts ({accounts.length})</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} · {a.bank_name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setAccountId("")}
                disabled={!accountId}
                data-testid="reports-all-accounts-btn"
                className="text-sm px-2 sm:px-3 py-2 border flex items-center gap-1.5 disabled:cursor-default transition-colors shrink-0"
                style={{
                  background: accountId ? "#FEE2E2" : "#FEF2F2",
                  borderColor: accountId ? "#F87171" : "#FECACA",
                  color: "#991B1B",
                  opacity: accountId ? 1 : 0.65,
                }}
                title={accountId ? "Show all accounts" : "All accounts already selected"}
              >
                <Layers size={13} style={{ color: "#DC2626" }} />
                <span className="hidden sm:inline">All accounts</span>
              </button>
            </div>
          </div>

          <div className="flex gap-3 w-full sm:w-auto sm:gap-3 items-end flex-wrap">
            <div className="flex-1 sm:flex-initial min-w-0">
              <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Period start</label>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }} data-testid="reports-start" />
            </div>
            <div className="flex-1 sm:flex-initial min-w-0">
              <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Period end</label>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }} data-testid="reports-end" />
            </div>
            <button onClick={() => { setStart(""); setEnd(""); }} className="text-xs underline text-[var(--ll-ink-soft)] py-2 shrink-0" data-testid="reports-clear">Clear period</button>
          </div>
        </div>

        {/* Scope summary */}
        <div className="text-xs text-[var(--ll-ink-soft)] mb-4" data-testid="reports-scope-summary">
          Showing{" "}
          <span className="font-medium text-[var(--ll-ink)]">
            {selectedAccount ? `${selectedAccount.name} (${selectedAccount.bank_name})` : `all ${accounts.length} bank account${accounts.length === 1 ? "" : "s"}`}
          </span>
          {(start || end) && (
            <> · period <span className="font-mono-fin text-[var(--ll-ink)]">{start || "all-time"} → {end || "present"}</span></>
          )}
        </div>

        {loading && <div className="ll-progress mb-4" />}

        {/* Period close package — bundles all 4 reports */}
        <div
          className="mb-5 border p-4 flex flex-wrap items-center gap-3"
          style={{ borderColor: "#1B4332", background: "#EFF6FF" }}
          data-testid="close-package-card"
        >
          <Package size={20} style={{ color: "#1B4332" }} className="shrink-0" />
          <div className="flex-1 w-full sm:w-auto sm:min-w-[260px]">
            <div className="font-display font-bold text-lg leading-tight">Period close package</div>
            <div className="text-sm text-[var(--ll-ink-soft)]">
              One file, all four statements (P&amp;L · Balance Sheet · Cash Flow · Expenses) with a cover summary — ready to email to your accountant or board.
            </div>
          </div>
          <button
            onClick={() => exportClosePackage("pdf")}
            data-testid="close-package-pdf-btn"
            className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5"
          >
            <FileText size={14} /> Download PDF
          </button>
          <button
            onClick={() => exportClosePackage("xlsx")}
            data-testid="close-package-xlsx-btn"
            className="text-sm px-4 py-2 border hover:bg-white flex items-center gap-1.5"
            style={{ borderColor: "#1B4332", background: "#DBEAFE", color: "#1B4332" }}
          >
            <FileSpreadsheet size={14} /> Download Excel
          </button>
          <button
            onClick={saveCurrentAsTemplate}
            data-testid="save-template-btn"
            className="text-sm px-3 py-2 border hover:bg-white flex items-center gap-1.5"
            style={{ borderColor: "#1B4332", color: "#1B4332" }}
            title="Save current scope + period as a reusable template"
          >
            <Star size={14} /> Save as template
          </button>
        </div>

        {/* Saved templates pills */}
        {templates.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="saved-templates">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mr-1 flex items-center gap-1">
              <Star size={11} /> Templates
            </span>
            {templates.map((t) => {
              const acc = accounts.find((a) => a.id === t.bank_account_id);
              const period = t.period_start || t.period_end ? `${t.period_start || "…"} → ${t.period_end || "…"}` : "Date Range submitted";
              const tooltip = `${acc ? `${acc.name} · ` : "All accounts · "}${period}`;
              return (
                <div
                  key={t.id}
                  data-testid={`template-pill-${t.id}`}
                  className="inline-flex items-stretch border"
                  style={{
                    borderColor: t.is_default ? "#F59E0B" : "#1B4332",
                    background: t.is_default ? "#FFFBEB" : "#FFFFFF",
                  }}
                >
                  <button
                    onClick={() => togglePin(t)}
                    title={t.is_default ? "Default — click to unpin" : "Pin as default (auto-apply on Reports load)"}
                    data-testid={`template-pin-${t.id}`}
                    className="px-2 border-r flex items-center hover:bg-[#FFFBEB]"
                    style={{ borderColor: t.is_default ? "#F59E0B" : "#BFDBFE" }}
                  >
                    <Pin
                      size={11}
                      style={{
                        color: t.is_default ? "#D97706" : "#94A3B8",
                        fill: t.is_default ? "#F59E0B" : "transparent",
                      }}
                    />
                  </button>
                  <button
                    onClick={() => applyTemplate(t)}
                    title={tooltip}
                    data-testid={`template-apply-${t.id}`}
                    className="text-xs px-3 py-1.5 hover:bg-[#EFF6FF] flex items-center gap-1.5"
                    style={{ color: "#1B4332" }}
                  >
                    {t.name}
                    {t.is_default && (
                      <span className="text-[9px] uppercase tracking-widest font-medium px-1.5 py-0.5" style={{ background: "#F59E0B", color: "white" }}>
                        Default
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => deleteTemplate(t)}
                    title="Delete template"
                    data-testid={`template-delete-${t.id}`}
                    className="px-2 border-l hover:bg-[var(--ll-error)] hover:text-white"
                    style={{ borderColor: t.is_default ? "#F59E0B" : "#BFDBFE" }}
                  >
                    <X size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="-mx-4 sm:mx-0 overflow-x-auto scrollbar-thin">
          <TabsList className="bg-[var(--ll-bg-sidebar)] border h-auto p-1 gap-1 inline-flex min-w-max" style={{ borderColor: "var(--ll-border)" }}>
            <TabsTrigger
              value="gl" data-testid="tab-gl"
              className="data-[state=active]:bg-[#EEF2FF] data-[state=active]:text-[#3730A3] data-[state=active]:border-[#818CF8] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              General Ledger
            </TabsTrigger>
            <TabsTrigger
              value="tb" data-testid="tab-tb"
              className="data-[state=active]:bg-[#F5F3FF] data-[state=active]:text-[#6B21A8] data-[state=active]:border-[#A78BFA] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              Trial Balance
            </TabsTrigger>
            <TabsTrigger
              value="is" data-testid="tab-is"
              className="data-[state=active]:bg-[#EFF6FF] data-[state=active]:text-[#1E40AF] data-[state=active]:border-[#3B82F6] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              Income Statement
            </TabsTrigger>
            <TabsTrigger
              value="pnl" data-testid="tab-pnl"
              className="data-[state=active]:bg-[#ECFDF5] data-[state=active]:text-[#065F46] data-[state=active]:border-[#10B981] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              Profit &amp; Loss
            </TabsTrigger>
            <TabsTrigger
              value="bs" data-testid="tab-bs"
              className="data-[state=active]:bg-[#FEFCE8] data-[state=active]:text-[#854D0E] data-[state=active]:border-[#EAB308] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              Balance Sheet
            </TabsTrigger>
            <TabsTrigger
              value="cf" data-testid="tab-cf"
              className="data-[state=active]:bg-[#FFF7ED] data-[state=active]:text-[#9A3412] data-[state=active]:border-[#FB923C] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              Cash Flow
            </TabsTrigger>
            <TabsTrigger
              value="exp" data-testid="tab-exp"
              className="data-[state=active]:bg-[#FEF2F2] data-[state=active]:text-[#991B1B] data-[state=active]:border-[#F87171] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              Expense Report
            </TabsTrigger>
            <TabsTrigger
              value="ar" data-testid="tab-ar"
              className="data-[state=active]:bg-[#FFF1F2] data-[state=active]:text-[#9F1239] data-[state=active]:border-[#FB7185] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              A/R Aging
            </TabsTrigger>
            <TabsTrigger
              value="t12" data-testid="tab-t12"
              className="data-[state=active]:bg-[#F5F3FF] data-[state=active]:text-[#5B21B6] data-[state=active]:border-[#A78BFA] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
            >
              T-12
            </TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="pnl" className="mt-5" data-testid="pnl-panel">
            {pnl && <PnLView pnl={pnl} onExport={exportReport} onShare={shareReportByEmail} />}
          </TabsContent>
          <TabsContent value="is" className="mt-5" data-testid="is-panel">
            {income && (
              <IncomeStatementView
                data={income}
                onExport={exportReport}
                onShare={shareReportByEmail}
                compareMode={isCompare}
                onCompareModeChange={setIsCompare}
              />
            )}
          </TabsContent>
          <TabsContent value="bs" className="mt-5" data-testid="bs-panel">
            {bs && (
              <BalanceSheetView
                bs={bs}
                onExport={exportReport}
                onShare={shareReportByEmail}
                onBsReload={load}
                compareMode={isCompare}
                onCompareModeChange={setIsCompare}
              />
            )}
          </TabsContent>
          <TabsContent value="cf" className="mt-5" data-testid="cf-panel">
            {cf && <CashFlowView cf={cf} onExport={exportReport} onShare={shareReportByEmail} />}
          </TabsContent>
          <TabsContent value="exp" className="mt-5" data-testid="exp-panel">
            {exp && <ExpenseView exp={exp} onExport={exportReport} onShare={shareReportByEmail} />}
          </TabsContent>
          <TabsContent value="t12" className="mt-5" data-testid="t12-panel">
            {t12 && <T12View data={t12} onExport={exportReport} onShare={shareReportByEmail} />}
          </TabsContent>
          <TabsContent value="ar" className="mt-5" data-testid="ar-panel">
            {arAging && <ARAgingView aging={arAging} onExport={exportReport} onShare={shareReportByEmail} asOf={end} onReload={load} />}
          </TabsContent>
          <TabsContent value="gl" className="mt-5" data-testid="gl-panel">
            {gl && (
              <GLView
                gl={gl}
                onExport={exportReport}
                onShare={shareReportByEmail}
                selectedCategories={glCategories}
                onSelectedCategoriesChange={setGlCategories}
                compareMode={glCompare}
                onCompareModeChange={setGlCompare}
              />
            )}
          </TabsContent>
          <TabsContent value="tb" className="mt-5" data-testid="tb-panel">
            {tb && <TrialBalanceView tb={tb} onExport={exportReport} onShare={shareReportByEmail} />}
          </TabsContent>
        </Tabs>
      </PageBody>

      {shareOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
          onClick={() => !sharing && setShareOpen(false)}
          data-testid="reports-share-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border w-full max-w-md p-6"
            style={{ borderColor: "var(--ll-ink)" }}
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="font-display font-bold text-2xl flex items-center gap-2">
                  <Send size={18} /> Share with client
                </h2>
                <p className="text-sm text-[var(--ll-ink-soft)] mt-1">
                  Generate the report file and publish it to your client&apos;s portal. They&apos;ll be notified in-app.
                </p>
              </div>
              <button
                onClick={() => setShareOpen(false)}
                disabled={sharing}
                aria-label="Close"
                data-testid="reports-share-close"
                className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="flex items-center gap-2 text-sm mb-2">
                  <input
                    type="checkbox"
                    checked={shareBulk}
                    onChange={(e) => setShareBulk(e.target.checked)}
                    data-testid="share-bulk-toggle"
                  />
                  <span>Publish to <strong>all {clients.length} active clients</strong> at once</span>
                </label>
              </div>
              {!shareBulk && (
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Client</label>
                  <select
                    value={shareForm.client_user_id}
                    onChange={(e) => setShareForm({ ...shareForm, client_user_id: e.target.value })}
                    data-testid="share-client-select"
                    className="w-full px-3 py-2 border bg-white text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <option value="">— Select a client —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
                    ))}
                  </select>
                  {clients.length === 0 && (
                    <div className="text-[11px] text-[var(--ll-ink-muted)] mt-1">
                      No client users yet. Invite a client from the Team page first.
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Report</label>
                  <select
                    value={shareForm.report_kind}
                    onChange={(e) => setShareForm({ ...shareForm, report_kind: e.target.value })}
                    data-testid="share-kind-select"
                    className="w-full px-3 py-2 border bg-white text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <option value="pnl">Profit & Loss</option>
                    <option value="balance_sheet">Balance Sheet</option>
                    <option value="expenses">Expense Report</option>
                    <option value="cash_flow">Cash Flow</option>
                    <option value="income_statement">Income Statement</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Format</label>
                  <select
                    value={shareForm.file_type}
                    onChange={(e) => setShareForm({ ...shareForm, file_type: e.target.value })}
                    data-testid="share-format-select"
                    className="w-full px-3 py-2 border bg-white text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <option value="pdf">PDF</option>
                    <option value="xlsx">Excel</option>
                  </select>
                </div>
              </div>
              <div className="text-[11px] text-[var(--ll-ink-muted)]">
                Period: {start || "all-time"} → {end || "today"} (uses current filters)
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Note (optional)</label>
                <textarea
                  rows={2}
                  value={shareForm.note}
                  onChange={(e) => setShareForm({ ...shareForm, note: e.target.value })}
                  placeholder="e.g. Q1 close review — please confirm the Uncategorized bucket."
                  data-testid="share-note-input"
                  maxLength={500}
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                onClick={() => setShareOpen(false)}
                disabled={sharing}
                data-testid="share-cancel"
                className="px-4 py-2 border text-sm"
                style={{ borderColor: "var(--ll-border)" }}
              >
                Cancel
              </button>
              <button
                onClick={shareReport}
                disabled={sharing || (!shareBulk && !shareForm.client_user_id)}
                data-testid="share-submit"
                className="px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm disabled:opacity-50 flex items-center gap-1.5"
              >
                {sharing
                  ? <><Loader2 size={13} className="animate-spin" /> Publishing…</>
                  : <><Send size={13} /> {shareBulk ? `Share with all ${clients.length}` : "Share report"}</>}
              </button>
            </div>
          </div>
        </div>
      )}
      <MailtoAttachmentModal
        open={!!mailtoDraft}
        onClose={() => setMailtoDraft(null)}
        {...(mailtoDraft || {})}
      />
    </>
  );
}

